import { useState, useRef } from 'react'
import { useLazyQuery, useMutation } from '@apollo/client/react'
import SearchYouTube from '../graphql/queries/SearchYouTube'
import AddTrackFromYouTube from '../graphql/queries/AddTrackFromYouTube'
import { Box, TextInput, Group, Stack, Text, Loader, Center, ActionIcon, Tooltip } from '@mantine/core'
import { useModalStack } from '../ui/ModalStack'

function formatDuration(seconds) {
    if (!seconds) return ''
    const m = Math.floor(seconds / 60)
    const s = seconds % 60
    return `${m}:${s.toString().padStart(2, '0')}`
}

export default function YouTubeSearch() {
    const [query, setQuery] = useState('')
    const [addingVideoId, setAddingVideoId] = useState(null)
    const [addedVideoIds, setAddedVideoIds] = useState(new Set())
    const [errorVideoId, setErrorVideoId] = useState(null)
    const inputRef = useRef(null)
    const { pushModal } = useModalStack()

    const [searchYouTube, { loading, data }] = useLazyQuery(SearchYouTube, {
        fetchPolicy: 'cache-and-network',
    })

    const [addTrackFromYouTube] = useMutation(AddTrackFromYouTube)

    const videos = data?.searchYouTube?.videos ?? []

    function handleSearch(e) {
        e.preventDefault()
        if (!query.trim()) return
        searchYouTube({ variables: { query: query.trim() } }).catch((err) => {
            const isQuotaError = err?.graphQLErrors?.some(
                (e) => e?.extensions?.code === 'YOUTUBE_QUOTA_EXCEEDED'
            )
            if (isQuotaError) {
                pushModal('alert', {
                    title: 'YouTube search unavailable',
                    text: "We've used up our YouTube search quota for today. Please try again tomorrow.",
                    okText: 'OK'
                })
            }
        })
    }

    async function handleAdd(video) {
        if (addingVideoId || addedVideoIds.has(video.videoId)) return
        setAddingVideoId(video.videoId)
        setErrorVideoId(null)
        try {
            const { data } = await addTrackFromYouTube({
                variables: { videoId: video.videoId }
            })
            const result = data?.addTrackFromYouTube
            setAddedVideoIds(prev => new Set(prev).add(video.videoId))
            if (result?.track) {
                pushModal('alert', {
                    title: result.alreadyExisted ? 'Already in catalog' : 'Added to catalog',
                    text: result.alreadyExisted
                        ? `"${result.track.title}" by ${result.track.artist} is already in the catalog.`
                        : `"${result.track.title}" by ${result.track.artist} has been added to the catalog.`,
                    okText: 'OK'
                })
            }
        } catch (err) {
            console.error('addTrackFromYouTube error:', err)
            setErrorVideoId(video.videoId)
        } finally {
            setAddingVideoId(null)
        }
    }

    return (
        <Box style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Search bar */}
            <Box
                p="sm"
                style={{
                    borderBottom: '1px solid rgba(255,255,255,0.08)',
                    backgroundColor: 'rgba(0,0,0,0.2)',
                }}
            >
                <form onSubmit={handleSearch}>
                    <Group gap="xs" wrap="nowrap">
                        <span className="material-symbols-outlined" style={{ color: 'var(--mantine-color-dimmed)' }}>
                            smart_display
                        </span>
                        <TextInput
                            ref={inputRef}
                            placeholder="Search YouTube..."
                            value={query}
                            onChange={(e) => setQuery(e.currentTarget.value)}
                            style={{ flex: 1 }}
                            autoFocus
                            disabled={loading}
                        />
                        <ActionIcon
                            type="submit"
                            variant="filled"
                            color="red"
                            aria-label="Search YouTube"
                            loading={loading}
                            disabled={loading}
                        >
                            <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>search</span>
                        </ActionIcon>
                    </Group>
                </form>
            </Box>

            {/* Results */}
            <Box style={{ flex: 1, overflowY: 'auto' }}>
                {loading && (
                    <Center p="xl">
                        <Loader size="md" />
                    </Center>
                )}
                {!loading && data && videos.length === 0 && (
                    <Box p="xl" ta="center">
                        <Text c="dimmed" size="lg">No results found.</Text>
                    </Box>
                )}
                {!loading && videos.length > 0 && (
                    <Stack gap={0}>
                        {videos.map((video) => {
                            const isAdding = addingVideoId === video.videoId
                            const isAdded = addedVideoIds.has(video.videoId)
                            const isError = errorVideoId === video.videoId

                            return (
                                <Box
                                    key={video.videoId}
                                    p="sm"
                                    style={{
                                        borderBottom: '1px solid rgba(255,255,255,0.05)',
                                        display: 'flex',
                                        gap: 'var(--mantine-spacing-sm)',
                                        alignItems: 'center',
                                        backgroundColor: 'transparent',
                                        transition: 'background-color 150ms ease',
                                    }}
                                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.03)' }}
                                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent' }}
                                >
                                    {/* Thumbnail */}
                                    <Box
                                        style={{
                                            width: 80,
                                            height: 52,
                                            flexShrink: 0,
                                            borderRadius: 'var(--mantine-radius-sm)',
                                            overflow: 'hidden',
                                            background: '#333',
                                            position: 'relative',
                                        }}
                                    >
                                        {video.thumbnailUrl ? (
                                            <img
                                                src={video.thumbnailUrl}
                                                alt={video.title}
                                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                            />
                                        ) : (
                                            <Center style={{ height: '100%' }}>
                                                <span className="material-symbols-outlined" style={{ color: '#666', fontSize: '24px' }}>
                                                    smart_display
                                                </span>
                                            </Center>
                                        )}
                                    </Box>

                                    {/* Info */}
                                    <Box style={{ flex: 1, minWidth: 0 }}>
                                        <Text
                                            size="sm"
                                            fw={500}
                                            style={{
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            <a
                                                href={`https://www.youtube.com/watch?v=${video.videoId}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{ color: 'inherit', textDecoration: 'none' }}
                                                onMouseEnter={(e) => { e.currentTarget.style.textDecoration = 'underline' }}
                                                onMouseLeave={(e) => { e.currentTarget.style.textDecoration = 'none' }}
                                            >
                                                {video.title}
                                            </a>
                                        </Text>
                                        <Group gap="xs" wrap="nowrap">
                                            <Text size="xs" c="dimmed" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {video.channelTitle}
                                            </Text>
                                            {video.durationSeconds > 0 && (
                                                <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>
                                                    {formatDuration(video.durationSeconds)}
                                                </Text>
                                            )}
                                        </Group>
                                    </Box>

                                    {/* Add button */}
                                    <Box style={{ flexShrink: 0 }}>
                                        {isAdded ? (
                                            <Tooltip label="Added to catalog" position="left">
                                                <span className="material-symbols-outlined" style={{ color: 'var(--mantine-color-green-5)', fontSize: '22px' }}>
                                                    check_circle
                                                </span>
                                            </Tooltip>
                                        ) : isError ? (
                                            <Tooltip label="Failed to add — try again" position="left">
                                                <ActionIcon
                                                    variant="subtle"
                                                    color="red"
                                                    onClick={() => handleAdd(video)}
                                                >
                                                    <span className="material-symbols-outlined">error</span>
                                                </ActionIcon>
                                            </Tooltip>
                                        ) : (
                                            <Tooltip label="Add to catalog" position="left" openDelay={500}>
                                                <ActionIcon
                                                    variant="subtle"
                                                    color="green"
                                                    onClick={() => handleAdd(video)}
                                                    loading={isAdding}
                                                    disabled={!!addingVideoId && !isAdding}
                                                >
                                                    <span className="material-symbols-outlined">add</span>
                                                </ActionIcon>
                                            </Tooltip>
                                        )}
                                    </Box>
                                </Box>
                            )
                        })}
                    </Stack>
                )}
                {!loading && !data && (
                    <Box p="xl" ta="center">
                        <Text c="dimmed" size="sm">
                            Search YouTube to find videos to add to the catalog.
                        </Text>
                    </Box>
                )}
            </Box>
        </Box>
    )
}


